import React, { useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Edit, Mail, Plus, Trash2, Palette, Wrench, Users, Eye, Receipt, Calendar, Clock, CheckCircle, XCircle, MinusCircle, StickyNote, Camera, MapPin, Phone, PhoneIncoming, DollarSign, CreditCard, FileText, ArrowUpDown, GripVertical, AlertTriangle, X } from 'lucide-react';
import { LoadingCard } from "../LoadingSpinner";
import CompanyCamTab from './CompanyCamTab';
import { formatDatePST, formatDateLongPST } from '../../utils/dateUtils';
import { formatPhone } from "../../utils/phoneUtils";
import { CreateChangeOrderDialog } from './CreateChangeOrderDialog';
import { EditChangeOrderDialog } from './EditChangeOrderDialog';
import { ViewChangeOrderDialog } from './ViewChangeOrderDialog';
import { SendPaymentReceiptDialog } from './SendPaymentReceiptDialog';
import { getChangeOrdersForProject, updateChangeOrder, deleteChangeOrder } from '../../services/changeOrderService';
import { emailService } from '../../services/emailService';
import UserMultiSelect from '../ui/UserMultiSelect';
import TeamResourceMultiSelect from '../ui/TeamResourceMultiSelect';
import { projectService } from '../../services/projectService';
import { productionService } from '../../services/productionService';
import { productionStageService } from '../../services/productionStageService';
import { crewService } from '../../services/crewService';
import { projectTimelineService } from '../../services/projectAppointmentService';
import { TimePicker } from "../ui/time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const getStatusLabel = (status) => {
    if (status) {
        return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'Unknown';
};

const getStatusColor = (status) => {
    switch (status) {
        case 'paid_complete': return 'bg-green-500';
        case 'construction_phase': return 'bg-yellow-500';
        case 'design_creation': return 'bg-blue-500';
        case 'on_hold': return 'bg-gray-500';
        case 'cancelled': return 'bg-red-500';
        default: return 'bg-gray-500';
    }
};

const ProjectDetailDialog = ({
    isDetailDialogOpen,
    setIsDetailDialogOpen,
    selectedProject,
    loadingProjectDetails,
    designers,
    subcontractors,
    // Handlers
    handleEditProject,
    handleSendEmail,
    refreshSelectedProject,
    // Payment/Change Order
    handleEditChangeOrder, handleDeleteChangeOrder, handleUpdatePayment, setIsPaymentDialogOpen, setEditingPayment,
    setIsChangeOrderDialogOpen, setNewChangeOrder, setEditingChangeOrderIndex,
    // Materials/Receipts
    setIsMaterialDialogOpen,
    handleEditMaterial, handleDeleteMaterial, setIsEditMaterialDialogOpen,
    setIsReceiptDialogOpen,
    handleEditReceipt, handleDeleteReceipt, setIsEditReceiptDialogOpen,
}) => {

    const { toast } = useToast();
    const [isCreateChangeOrderOpen, setIsCreateChangeOrderOpen] = useState(false);
    const [isEditChangeOrderOpen, setIsEditChangeOrderOpen] = useState(false);
    const [isViewChangeOrderOpen, setIsViewChangeOrderOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [changeOrderToDelete, setChangeOrderToDelete] = useState(null);
    const [changeOrderToEdit, setChangeOrderToEdit] = useState(null);
    const [insertingPayment, setInsertingPayment] = useState(false);
    const [generatingPayment, setGeneratingPayment] = useState(false);

    // Sort state for materials and receipts tables
    const [materialSort, setMaterialSort] = useState({ field: 'name', direction: 'asc' });
    const [receiptSort, setReceiptSort] = useState({ field: 'item', direction: 'asc' });

    // Production workflow state
    const [productionStages, setProductionStages] = useState([]);
    const [crews, setCrews] = useState([]);
    const [productionBusy, setProductionBusy] = useState(false);

    useEffect(() => {
        if (!isDetailDialogOpen) return;
        let cancelled = false;
        const loadProductionMeta = async () => {
            try {
                const [stagesRes, crewsRes] = await Promise.all([
                    productionStageService.getAll(),
                    crewService.getAll(true),
                ]);
                if (cancelled) return;
                setProductionStages(stagesRes?.data || []);
                setCrews(crewsRes?.data || []);
            } catch (e) {
                if (!cancelled) {
                    console.error('Failed to load production metadata', e);
                }
            }
        };
        loadProductionMeta();
        return () => { cancelled = true; };
    }, [isDetailDialogOpen]);

    const currentProductionStage = useMemo(() => {
        const stageValue = selectedProject?.current_production_stage;
        if (!stageValue) return null;
        return productionStages.find(s => s.value === stageValue) || null;
    }, [selectedProject, productionStages]);

    const handleStartProduction = async () => {
        if (!selectedProject) return;
        try {
            setProductionBusy(true);
            await productionService.startProduction(selectedProject.id || selectedProject._id);
            toast({ description: 'Project added to production workflow' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e) {
            toast({ variant: 'destructive', description: e?.detail || 'Failed to start production' });
        } finally {
            setProductionBusy(false);
        }
    };

    const handleStopProduction = async () => {
        if (!selectedProject) return;
        if (!window.confirm('Remove this project from the production workflow?')) return;
        try {
            setProductionBusy(true);
            await productionService.stopProduction(selectedProject.id || selectedProject._id);
            toast({ description: 'Project removed from production workflow' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e) {
            toast({ variant: 'destructive', description: e?.detail || 'Failed to stop production' });
        } finally {
            setProductionBusy(false);
        }
    };

    const handleUpdateProductionStage = async (stageValue, payload) => {
        if (!selectedProject) return;
        try {
            setProductionBusy(true);
            await productionService.updateStage(selectedProject.id || selectedProject._id, stageValue, payload);
            toast({ description: 'Stage updated' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e) {
            toast({ variant: 'destructive', description: e?.detail || 'Failed to update stage' });
        } finally {
            setProductionBusy(false);
        }
    };

    const handleSyncProductionStages = async () => {
        if (!selectedProject) return;
        try {
            setProductionBusy(true);
            await productionService.syncStages(selectedProject.id || selectedProject._id);
            toast({ description: 'Stages synced with current definitions' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e) {
            toast({ variant: 'destructive', description: e?.detail || 'Failed to sync stages' });
        } finally {
            setProductionBusy(false);
        }
    };

    const handleAddOptionalStage = async (stageValue) => {
        if (!selectedProject) return;
        try {
            setProductionBusy(true);
            await productionService.addOptionalStage(selectedProject.id || selectedProject._id, stageValue);
            toast({ description: 'Optional stage added' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e) {
            toast({ variant: 'destructive', description: e?.detail || 'Failed to add stage' });
        } finally {
            setProductionBusy(false);
        }
    };

    // Drag-and-drop state for optional stage reordering
    const [draggedStageValue, setDraggedStageValue] = useState(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);

    const handleStageDragStart = (e, stageValue) => {
        setDraggedStageValue(stageValue);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleStageDragOver = (e, idx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIdx(idx);
    };

    const handleStageDrop = async (e, dropIdx) => {
        e.preventDefault();
        setDragOverIdx(null);
        if (!draggedStageValue || !selectedProject) {
            setDraggedStageValue(null);
            return;
        }
        const stages = [...(selectedProject.production_stages || [])];
        const fromIdx = stages.findIndex(s => s.stage_value === draggedStageValue);
        if (fromIdx === -1 || fromIdx === dropIdx) {
            setDraggedStageValue(null);
            return;
        }
        // Remove dragged item and insert at drop position
        const [moved] = stages.splice(fromIdx, 1);
        const insertIdx = dropIdx > fromIdx ? dropIdx - 1 : dropIdx;
        stages.splice(insertIdx, 0, moved);
        const newOrder = stages.map(s => s.stage_value);
        setDraggedStageValue(null);
        try {
            setProductionBusy(true);
            await productionService.reorderStages(selectedProject.id || selectedProject._id, newOrder);
            toast({ description: 'Stage moved' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e2) {
            toast({ variant: 'destructive', description: e2?.detail || 'Failed to reorder' });
        } finally {
            setProductionBusy(false);
        }
    };

    const handleStageDragEnd = () => {
        setDraggedStageValue(null);
        setDragOverIdx(null);
    };

    const handleRemoveOptionalStage = async (stageValue) => {
        if (!selectedProject) return;
        if (!window.confirm('Remove this optional stage from the project?')) return;
        try {
            setProductionBusy(true);
            await productionService.removeOptionalStage(selectedProject.id || selectedProject._id, stageValue);
            toast({ description: 'Optional stage removed' });
            if (refreshSelectedProject) await refreshSelectedProject();
        } catch (e) {
            toast({ variant: 'destructive', description: e?.detail || 'Failed to remove stage' });
        } finally {
            setProductionBusy(false);
        }
    };

    const handleMaterialSort = (field) => {
        setMaterialSort(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleReceiptSort = (field) => {
        setReceiptSort(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortItems = (items, sort) => {
        return [...items].sort((a, b) => {
            let aVal = a[sort.field] || '';
            let bVal = b[sort.field] || '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
            if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const SortHeader = ({ label, field, sort, onSort }) => (
        <TableHead
            className="cursor-pointer select-none hover:bg-muted/50 text-xs"
            onClick={() => onSort(field)}
        >
            <span className="flex items-center gap-1">
                {label}
                {sort.field === field ? (sort.direction === 'asc' ? ' \u25B2' : ' \u25BC') : <ArrowUpDown className="h-3 w-3 opacity-30" />}
            </span>
        </TableHead>
    );

    // Handler for inserting extra payment — prompts for a custom name
    const handleInsertExtraPayment = async () => {
        if (!selectedProject) return;

        const userName = window.prompt(
            "Name this payment (leave blank to auto-name as the next progress payment):",
            ""
        );
        // null = user cancelled the prompt; empty string = use auto-name
        if (userName === null) return;

        try {
            setInsertingPayment(true);
            const projectId = selectedProject.id || selectedProject._id;
            const result = await projectService.insertExtraPayment(projectId, userName.trim() || null);
            toast({ description: result?.message || "Extra payment inserted successfully!" });
            await refreshSelectedProject();
        } catch (error) {
            console.error("Failed to insert extra payment:", error);
            toast({ variant: "destructive", description: error?.detail || error.message || "Failed to insert extra payment" });
        } finally {
            setInsertingPayment(false);
        }
    };

    // Handler for deleting a manually-added extra payment
    const handleDeleteExtraPayment = async (payment) => {
        if (!selectedProject) return;
        if (!window.confirm(`Delete "${payment.description}"? This cannot be undone.`)) return;

        try {
            const projectId = selectedProject.id || selectedProject._id;
            const result = await projectService.deleteExtraPayment(projectId, payment.payment_number);
            toast({ description: result?.message || "Payment deleted" });
            await refreshSelectedProject();
        } catch (error) {
            console.error("Failed to delete payment:", error);
            toast({ variant: "destructive", description: error?.detail || error.message || "Failed to delete payment" });
        }
    };

    // Handler for generating payment schedule when none exists
    const handleGeneratePaymentSchedule = async () => {
        if (!selectedProject) return;

        try {
            setGeneratingPayment(true);
            const projectId = selectedProject.id || selectedProject._id;
            const contractPrice = selectedProject.contract_price || selectedProject.budget || 0;

            if (contractPrice <= 0) {
                toast({
                    variant: "destructive",
                    description: "Please set a contract price first (edit the project)."
                });
                return;
            }

            const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
            const token = localStorage.getItem("token");
            const response = await fetch(`${backendUrl}/api/projects/${projectId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    contract_price: contractPrice
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to generate payment schedule');
            }

            toast({ description: "Payment schedule generated successfully!" });
            await refreshSelectedProject();
        } catch (error) {
            console.error("Failed to generate payment schedule:", error);
            toast({
                variant: "destructive",
                description: error.message || "Failed to generate payment schedule"
            });
        } finally {
            setGeneratingPayment(false);
        }
    };

    const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
    const [changeOrders, setChangeOrders] = useState([]);
    const [loadingChangeOrders, setLoadingChangeOrders] = useState(false);

    // Notes state
    const [newNoteContent, setNewNoteContent] = useState('');
    const [addingNote, setAddingNote] = useState(false);

    // Callback log state (standalone collection)
    const [showCallbackForm, setShowCallbackForm] = useState(false);
    const [addingCallback, setAddingCallback] = useState(false);
    const [projectCallbacks, setProjectCallbacks] = useState([]);
    const [newCallback, setNewCallback] = useState({
        caller_name: '',
        contact_method: 'phone',
        reason: '',
        urgency: 'normal',
    });

    // New state for payment receipt dialog
    const [isSendReceiptDialogOpen, setIsSendReceiptDialogOpen] = useState(false);
    const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);
    
    // Timeline event states
    const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
    const [isDeleteEventDialogOpen, setIsDeleteEventDialogOpen] = useState(false);
    const [editingEventIndex, setEditingEventIndex] = useState(null);
    const [newEvent, setNewEvent] = useState({
        type: 'event',  // 'event' or 'appointment'
        title: '',
        description: '',
        scheduled_date: '',
        scheduled_time: ''
    });
    
    // Email history state
    const [emailHistory, setEmailHistory] = useState([]);
    const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);

    const customerName = selectedProject?.customer?.name || selectedProject?.customer || 'N/A';

    // Fetch change orders for the project
    const fetchChangeOrders = async () => {
        if (!selectedProject?.id && !selectedProject?._id) return;
        
        const projectId = selectedProject.id || selectedProject._id;
        setLoadingChangeOrders(true);
        
        try {
            const changeOrdersData = await getChangeOrdersForProject(projectId);
            // axios interceptor already returns response.data, so changeOrdersData is the array
            setChangeOrders(changeOrdersData || []);
        } catch (error) {
            console.error('Error fetching change orders:', error);
            toast({
                title: "Error",
                description: "Failed to fetch change orders.",
                variant: "destructive"
            });
        } finally {
            setLoadingChangeOrders(false);
        }
    };

    // Fetch email history for the project
    const fetchEmailHistory = useCallback(async () => {
        if (!selectedProject?.id && !selectedProject?._id) return;
        const projectId = selectedProject.id || selectedProject._id;
        setLoadingEmailHistory(true);
        try {
            // Use getAll with a project_id param
            const response = await emailService.getAll({ project_id: projectId });
            // Response structure: {data: {data: [...], total, page, limit}}
            setEmailHistory(response?.data?.data || []); 
        } catch (error) {
            console.error('Error fetching email history:', error);
            toast({
                title: "Error",
                description: "Failed to fetch email history.",
                variant: "destructive"
            });
            // Ensure emailHistory remains an array even on error
            setEmailHistory([]);
        } finally {
            setLoadingEmailHistory(false);
        }
    }, [selectedProject, toast]);

    // Add note function using dedicated endpoint
    const handleAddNote = async () => {
        if (!selectedProject || !newNoteContent.trim()) return;
        
        try {
            setAddingNote(true);
            const projectId = selectedProject.id || selectedProject._id;
            
            // Use same pattern as api.js - fallback to window.location.origin for production
            const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
            const response = await fetch(`${backendUrl}/api/projects/${projectId}/notes`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ content: newNoteContent.trim() })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to add note');
            }
            
            toast({
                description: "Note added successfully!"
            });
            setNewNoteContent('');
            
            // Refresh project data
            if (refreshSelectedProject) {
                await refreshSelectedProject();
            }
        } catch (error) {
            console.error("Failed to add note:", error);
            toast({
                variant: "destructive",
                description: error.message || "Failed to add note"
            });
        } finally {
            setAddingNote(false);
        }
    };

    // Delete note function using dedicated endpoint
    const handleDeleteNote = async (noteId) => {
        if (!selectedProject || !noteId) return;
        
        try {
            const projectId = selectedProject.id || selectedProject._id;
            
            const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
            const response = await fetch(`${backendUrl}/api/projects/${projectId}/notes/${noteId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to delete note');
            }
            
            toast({
                description: "Note deleted"
            });
            
            // Refresh project data
            if (refreshSelectedProject) {
                await refreshSelectedProject();
            }
        } catch (error) {
            console.error("Failed to delete note:", error);
            toast({
                variant: "destructive",
                description: error.message || "Failed to delete note"
            });
        }
    };

    // Fetch callbacks for this project from standalone collection
    const fetchProjectCallbacks = async () => {
        if (!selectedProject) return;
        try {
            const projectId = selectedProject.id || selectedProject._id;
            const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
            const response = await fetch(`${backendUrl}/api/callbacks/?project_id=${projectId}&limit=50`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
            });
            if (response.ok) {
                const result = await response.json();
                setProjectCallbacks(result?.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch callbacks:", error);
        }
    };

    // Add callback to standalone collection
    const handleAddCallback = async () => {
        if (!selectedProject || !newCallback.reason.trim()) return;

        try {
            setAddingCallback(true);
            const projectId = selectedProject.id || selectedProject._id;

            const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
            const response = await fetch(`${backendUrl}/api/callbacks/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    project_id: projectId,
                    issue_description: newCallback.reason,
                    urgency: newCallback.urgency,
                    reported_by: newCallback.caller_name,
                    reported_via: newCallback.contact_method,
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to log callback');
            }

            toast({ description: "Callback logged successfully!" });
            setNewCallback({ caller_name: '', contact_method: 'phone', reason: '', urgency: 'normal' });
            setShowCallbackForm(false);
            await fetchProjectCallbacks();
        } catch (error) {
            console.error("Failed to add callback:", error);
            toast({ variant: "destructive", description: error.message || "Failed to log callback" });
        } finally {
            setAddingCallback(false);
        }
    };

    // Delete callback from standalone collection
    const handleDeleteCallback = async (callbackId) => {
        if (!callbackId) return;

        try {
            const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
            const response = await fetch(`${backendUrl}/api/callbacks/${callbackId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to delete callback');
            }

            toast({ description: "Callback deleted" });
            await fetchProjectCallbacks();
        } catch (error) {
            console.error("Failed to delete callback:", error);
            toast({ variant: "destructive", description: error.message || "Failed to delete callback" });
        }
    };

    // Handler to open send receipt dialog
    const handleOpenSendReceipt = (payment) => {
        setSelectedPaymentForReceipt(payment);
        setIsSendReceiptDialogOpen(true);
    };

    // Handle status change for change orders
    const handleStatusChange = async (changeOrderId, newStatus) => {
        try {
            await updateChangeOrder(changeOrderId, { status: newStatus });
            toast({
                title: "Success",
                description: `Change order marked as ${newStatus}.`
            });
            
            // Refresh both project details and change orders
            if (refreshSelectedProject) {
                refreshSelectedProject();
            }
            fetchChangeOrders();
        } catch (error) {
            console.error('Error updating change order status:', error);
            toast({
                title: "Error",
                description: "Failed to update change order status.",
                variant: "destructive"
            });
        }
    };

    // Handle viewing a change order
    const handleViewChangeOrder = (changeOrder) => {
        setSelectedChangeOrder(changeOrder);
        setIsViewChangeOrderOpen(true);
    };

    // Handle editing a change order
    const handleEditChangeOrderDocument = (changeOrder) => {
        setChangeOrderToEdit(changeOrder);
        setIsEditChangeOrderOpen(true);
    };

    // Handle deleting a change order document - show confirmation first
    const handleDeleteChangeOrderDocument = (changeOrderId) => {
        setChangeOrderToDelete(changeOrderId);
        setIsDeleteConfirmOpen(true);
    };

    // Confirm and execute deletion
    const confirmDeleteChangeOrder = async () => {
        if (!changeOrderToDelete) return;

        try {
            await deleteChangeOrder(changeOrderToDelete);
            toast({
                title: "Success",
                description: "Change order deleted successfully."
            });
            
            // Refresh both project details and change orders
            if (refreshSelectedProject) {
                refreshSelectedProject();
            }
            fetchChangeOrders();
        } catch (error) {
            console.error('Error deleting change order:', error);
            toast({
                title: "Error",
                description: "Failed to delete change order.",
                variant: "destructive"
            });
        } finally {
            setIsDeleteConfirmOpen(false);
            setChangeOrderToDelete(null);
        }
    };
    
    // ==================== Timeline Event Handlers ====================
    
    const handleAddEvent = () => {
        setEditingEventIndex(null);
        setNewEvent({
            type: 'event',
            title: '',
            description: '',
            scheduled_date: '',
            scheduled_time: ''
        });
        setIsEventDialogOpen(true);
    };

    const handleEditEvent = (event, index) => {
        // Parse the event activity text to extract details
        const activity = event.activity || '';
        // Support both old emoji format (📅, 📌) and new text format ([APT], [EVENT])
        const isAppointment = activity.startsWith('📅') || activity.startsWith('[APT]');
        
        // Extract title - support both emoji and text marker formats
        let titleMatch = activity.match(/\[APT\]\s*(.+?):/);
        if (!titleMatch) titleMatch = activity.match(/\[EVENT\]\s*(.+?):/);
        if (!titleMatch) titleMatch = activity.match(/[📅📌]\s*(.+?):/);
        const title = titleMatch ? titleMatch[1].trim() : '';
        
        // Extract description (text between colon and " - Scheduled")
        const descMatch = activity.match(/:\s*(.+?)\s*-\s*Scheduled/);
        const description = descMatch ? descMatch[1].trim() : '';
        
        // Extract date
        const dateMatch = activity.match(/Scheduled for (\d{4}-\d{2}-\d{2})/);
        const scheduled_date = dateMatch ? dateMatch[1] : '';
        
        // Extract time (if appointment)
        const timeMatch = activity.match(/at (\d{2}:\d{2})/);
        const scheduled_time = timeMatch ? timeMatch[1] : '';
        
        setEditingEventIndex(index);
        setNewEvent({
            type: isAppointment ? 'appointment' : 'event',
            title,
            description,
            scheduled_date,
            scheduled_time
        });
        setIsEventDialogOpen(true);
    };
    
    const handleSaveEvent = async () => {
        // Validate based on type
        if (!newEvent.title || !newEvent.scheduled_date) {
            toast({
                title: "Validation Error",
                description: "Please fill in title and date",
                variant: "destructive"
            });
            return;
        }
        
        // If appointment type, require time
        if (newEvent.type === 'appointment' && !newEvent.scheduled_time) {
            toast({
                title: "Validation Error",
                description: "Appointments require a time",
                variant: "destructive"
            });
            return;
        }
        
        try {
            const projectId = selectedProject.id || selectedProject._id;
            
            if (editingEventIndex !== null) {
                // Update existing event
                await projectTimelineService.updateTimelineEvent(projectId, editingEventIndex, newEvent);
                toast({
                    description: "Event updated successfully"
                });
            } else {
                // Add new event to timeline
                await projectTimelineService.addTimelineEvent(projectId, newEvent);
                toast({
                    description: "Event added to timeline successfully"
                });
            }
            
            // Refresh project data
            await refreshSelectedProject();
            setIsEventDialogOpen(false);
        } catch (error) {
            console.error('Error saving event:', error);
            toast({
                title: "Error",
                description: error?.response?.data?.detail || "Failed to save event",
                variant: "destructive"
            });
        }
    };

    const handleDeleteEvent = (index) => {
        setEditingEventIndex(index);
        setIsDeleteEventDialogOpen(true);
    };

    const confirmDeleteEvent = async () => {
        try {
            const projectId = selectedProject.id || selectedProject._id;
            await projectTimelineService.deleteTimelineEvent(projectId, editingEventIndex);
            
            toast({
                description: "Event deleted successfully"
            });
            
            // Refresh project data
            await refreshSelectedProject();
            setIsDeleteEventDialogOpen(false);
        } catch (error) {
            console.error('Error deleting event:', error);
            toast({
                title: "Error",
                description: "Failed to delete event",
                variant: "destructive"
            });
        }
    };

    // Format currency helper
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    // Combined and sorted timeline events
    const sortedTimelineEvents = useMemo(() => {
        const events = [];

        // Project Start
        if (selectedProject?.start_date) {
            events.push({
                type: 'start',
                date: selectedProject.start_date,
                title: 'Project Started',
                description: `Project formally began.`,
                color: 'bg-green-500'
            });
        }

        // Payments (Due)
        selectedProject?.payment_schedule?.forEach(p => {
            if (p.due_date) {
                events.push({
                    type: 'payment_due',
                    date: p.due_date,
                    title: `${p.description} Due`,
                    description: `Amount: ${formatCurrency(p.amount)}`,
                    color: 'bg-yellow-500'
                });
            }
        });

        // Payments (Paid)
        selectedProject?.payment_schedule?.forEach(p => {
            if (p.paid_date) {
                events.push({
                    type: 'payment_paid',
                    date: p.paid_date,
                    title: `${p.description} Paid`,
                    description: `Amount: ${formatCurrency(p.amount)}`,
                    color: 'bg-green-500'
                });
            }
        });

        // Change Orders (Manual Log)
        selectedProject?.change_orders?.forEach(co => {
            events.push({
                type: 'change_order_manual',
                date: co.date,
                title: 'Change Order Added',
                description: `${co.scope_of_work} - +${formatCurrency(co.amount)}`,
                color: 'bg-orange-500'
            });
        });

        // Email History
        emailHistory.forEach(email => {
            events.push({
                type: 'email',
                date: email.sent_at,
                title: `Email Sent to ${email.to_name}`,
                description: `Subject: ${email.subject}`,
                color: 'bg-cyan-500'
            });
        });

        // Activity Log (Timeline Events/Appointments added manually)
        selectedProject?.activity_log?.forEach(activity => {
            // Format display text - convert text markers to emojis for display
            let displayTitle = activity.activity || '';
            let eventColor = 'bg-purple-500';
            if (displayTitle.startsWith('[APT]')) {
                displayTitle = '📅 ' + displayTitle.replace('[APT] ', '');
                eventColor = 'bg-blue-500';
            } else if (displayTitle.startsWith('[EVENT]')) {
                displayTitle = '📌 ' + displayTitle.replace('[EVENT] ', '');
                eventColor = 'bg-purple-500';
            }
            
            events.push({
                type: 'activity',
                date: activity.date,
                title: displayTitle,
                description: `Added by ${activity.user}`,
                color: eventColor
            });
        });

        // Sort all events descending by date
        return events.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [selectedProject, emailHistory]);

    // Fetch change orders and email history when dialog opens
    // Note: fetchChangeOrders and fetchEmailHistory are intentionally excluded from deps to prevent infinite loop
    useEffect(() => {
        if (isDetailDialogOpen && selectedProject) {
            fetchChangeOrders();
            fetchEmailHistory();
            fetchProjectCallbacks();
        }
    }, [isDetailDialogOpen, selectedProject]);

    return (
        <>
            <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
                <DialogContent
                    className="max-h-[100dvh] w-screen md:max-w-4xl md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="sticky top-0 z-10 bg-white border-b p-4 sm:p-6 flex-shrink-0">
                        <DialogHeader>
                            <DialogTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <span className="text-lg sm:text-xl font-bold truncate">{selectedProject?.name}</span>
                                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                                    <Button
                                        size="sm"
                                        onClick={() => handleSendEmail(selectedProject)}
                                        className="bg-brand-600 hover:bg-brand-700 h-10 sm:h-8"
                                    >
                                        <Mail className="mr-1 h-4 w-4" />
                                        Email
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditProject(selectedProject)}
                                        className="h-10 sm:h-8"
                                    >
                                        <Edit className="mr-1 h-4 w-4" />
                                        Edit
                                    </Button>
                                </div>
                            </DialogTitle>
                            <DialogDescription className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
                                <span className="truncate">Customer: {customerName}</span> <span>|</span> Status: <Badge className={`${getStatusColor(selectedProject?.status)} text-white`}>{getStatusLabel(selectedProject?.status)}</Badge>
                                {selectedProject?.in_production && currentProductionStage && (
                                    <>
                                        <span>|</span>
                                        <Badge
                                            className="text-white"
                                            style={{ backgroundColor: currentProductionStage.color || '#64748b' }}
                                        >
                                            {currentProductionStage.label}
                                        </Badge>
                                    </>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                {loadingProjectDetails ? (
                    <div className="p-4 sm:p-6"><LoadingCard>Loading project details...</LoadingCard></div>
                ) : (
                    selectedProject && (
                        <Tabs defaultValue="overview" className="w-full flex flex-col flex-1 min-h-0">
                            <div className="sticky top-0 z-10 bg-white border-b px-4 flex-shrink-0">
                                <div className="overflow-x-auto">
                                    <TabsList className="inline-flex w-auto min-w-full flex-nowrap whitespace-nowrap overflow-x-auto overflow-y-hidden">
                                        <TabsTrigger value="overview" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Overview</TabsTrigger>
                                        <TabsTrigger value="payments" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Payments</TabsTrigger>
                                        <TabsTrigger value="materials" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Materials</TabsTrigger>
                                        <TabsTrigger value="receipts" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Receipts</TabsTrigger>
                                        <TabsTrigger value="events" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Events</TabsTrigger>
                                        <TabsTrigger value="timeline" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Timeline</TabsTrigger>
                                        <TabsTrigger value="team" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Team</TabsTrigger>
                                        <TabsTrigger value="production" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Production</TabsTrigger>
                                        <TabsTrigger value="callbacks" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Callbacks</TabsTrigger>
                                        <TabsTrigger value="companycam" className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5">Notes/Pics</TabsTrigger>
                                    </TabsList>
                                </div>
                            </div>

                            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                            {/* OVERVIEW TAB */}
                            <TabsContent value="overview" className="space-y-4 mt-0">
                                {/* Financial Summary Cards */}
                                {selectedProject.project_type === "design_only" ? (
                                    <div className="grid grid-cols-1 gap-3">
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Design Fee</div>
                                            <div className="text-2xl sm:text-3xl font-bold mt-1">
                                                ${(selectedProject.contract_price || selectedProject.budget || 0).toLocaleString()}
                                            </div>
                                            {selectedProject.estimated_construction_budget > 0 && (
                                                <div className="text-xs text-muted-foreground mt-2">
                                                    Est. Construction Budget: ${selectedProject.estimated_construction_budget.toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Total Contract</div>
                                            <div className="text-2xl sm:text-3xl font-bold mt-1">
                                                ${(selectedProject.total_job_cost || selectedProject.contract_price || selectedProject.budget || 0).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Paid to Date</div>
                                            <div className="text-2xl sm:text-3xl font-bold mt-1 text-green-600">
                                                ${(selectedProject.payment_schedule?.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0) || 0).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Outstanding</div>
                                            {(() => {
                                                const totalContract = selectedProject.total_job_cost || selectedProject.contract_price || selectedProject.budget || 0;
                                                const paidAmount = selectedProject.payment_schedule?.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
                                                const outstanding = totalContract - paidAmount;
                                                const progressPercent = totalContract > 0 ? (paidAmount / totalContract) * 100 : 0;
                                                return (
                                                    <>
                                                        <div className={`text-2xl sm:text-3xl font-bold mt-1 ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                            ${outstanding.toLocaleString()}
                                                        </div>
                                                        <Progress value={progressPercent} className="h-1.5 mt-2" />
                                                        <div className="text-xs text-muted-foreground mt-1">{Math.round(progressPercent)}% collected</div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {/* Two-column layout: Project Details + Customer/Assigned */}
                                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                                    {/* Left Column - Project Details */}
                                    <Card className="lg:col-span-3">
                                        <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
                                            <CardTitle className="text-base">Project Details</CardTitle>
                                        </CardHeader>
                                        <CardContent className="p-4 sm:p-6 pt-0">
                                            {/* Key details grid */}
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                                {selectedProject.company && (
                                                    <div>
                                                        <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Company</div>
                                                        <div className="text-sm font-medium mt-1">
                                                            <Badge className={selectedProject.company === "Landscaping" ? "bg-blue-500 hover:bg-blue-600" : "bg-red-500 hover:bg-red-600"}>
                                                                {selectedProject.company}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Type</div>
                                                    <div className="text-sm font-medium mt-1">{selectedProject.project_type === "design_only" ? "Design Only" : "Construction"}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Status</div>
                                                    <div className="mt-1">
                                                        <Badge className={getStatusColor(selectedProject.status)}>
                                                            {getStatusLabel(selectedProject.status)}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Priority</div>
                                                    <div className="text-sm font-medium mt-1">{selectedProject.priority || 'Not set'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Contract Sign</div>
                                                    <div className="text-sm font-medium mt-1">{selectedProject.contract_sign_date ? formatDatePST(selectedProject.contract_sign_date) : 'Not set'}</div>
                                                </div>
                                                {(() => {
                                                    const projectId = selectedProject.id || selectedProject._id;
                                                    const saveDate = async (field, value) => {
                                                        try {
                                                            await projectService.update(projectId, { [field]: value || null });
                                                            selectedProject[field] = value || null;
                                                            await refreshSelectedProject();
                                                        } catch (err) {
                                                            console.error("Failed to save date:", err);
                                                            toast({ variant: "destructive", description: "Failed to save date" });
                                                        }
                                                    };
                                                    return (
                                                        <>
                                                            <div>
                                                                <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Start Date</div>
                                                                <Input
                                                                    type="date"
                                                                    defaultValue={selectedProject.start_date || ""}
                                                                    className="mt-1 h-8 px-2 text-sm font-medium text-brand-600 border-transparent hover:border-input focus-visible:border-input focus-visible:ring-1"
                                                                    onBlur={(e) => {
                                                                        if (e.target.value !== (selectedProject.start_date || "")) {
                                                                            saveDate("start_date", e.target.value);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Est. End Date</div>
                                                                <Input
                                                                    type="date"
                                                                    defaultValue={selectedProject.end_date || ""}
                                                                    className="mt-1 h-8 px-2 text-sm font-medium border-transparent hover:border-input focus-visible:border-input focus-visible:ring-1"
                                                                    onBlur={(e) => {
                                                                        if (e.target.value !== (selectedProject.end_date || "")) {
                                                                            saveDate("end_date", e.target.value);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>

                                            {/* Jobsite Address */}
                                            <div className="mt-4 pt-4 border-t">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Jobsite Address</div>
                                                {(() => {
                                                    const fullAddress = `${selectedProject.jobsite_address || selectedProject.address || 'No street address'}${selectedProject.city ? `, ${selectedProject.city}` : ''}${selectedProject.zip ? ` ${selectedProject.zip}` : ''}`;
                                                    const hasAddress = selectedProject.jobsite_address || selectedProject.address;
                                                    return hasAddress ? (
                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5"
                                                        >
                                                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                                            {fullAddress}
                                                        </a>
                                                    ) : (
                                                        <div className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                                                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                                            No address set
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {/* Description */}
                                            {selectedProject.description && (
                                                <div className="mt-4 pt-4 border-t">
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Description</div>
                                                    <p className="text-sm text-gray-700">{selectedProject.description}</p>
                                                </div>
                                            )}

                                            {/* Services */}
                                            {selectedProject.services && selectedProject.services.length > 0 && (
                                                <div className="mt-4 pt-4 border-t">
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2">Services</div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {selectedProject.services.map((service, index) => (
                                                            <Badge key={index} variant="outline">{service}</Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Financials section */}
                                            {(() => {
                                                const baseContract = parseFloat(selectedProject.contract_price || selectedProject.budget || 0);
                                                const changeOrders = selectedProject.change_orders || [];
                                                const changeOrdersTotal = changeOrders.reduce((s, co) => s + (parseFloat(co?.amount) || 0), 0);
                                                const totalContract = baseContract + changeOrdersTotal;
                                                const amountSpent = parseFloat(selectedProject.spent || 0);
                                                const payrollEst = parseFloat(selectedProject.crew_payroll_estimate || 0);
                                                const payrollActual = parseFloat(selectedProject.crew_payroll_actual || 0);
                                                // Only subtract payroll when actual is filled in. Estimate is informational only.
                                                const netProfit = totalContract - amountSpent - payrollActual;
                                                const margin = totalContract > 0 ? (netProfit / totalContract) * 100 : 0;
                                                const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                                                const Row = ({ label, value, color = "", sub = null, bold = false }) => (
                                                    <div className="flex items-baseline justify-between py-1 text-sm">
                                                        <span className="text-gray-600">{label}{sub && <span className="text-xs text-gray-400 ml-1">{sub}</span>}</span>
                                                        <span className={`${color} ${bold ? "font-bold" : "font-medium"}`}>{value}</span>
                                                    </div>
                                                );

                                                return (
                                                    <div className="mt-4 pt-4 border-t">
                                                        <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2">Financials</div>
                                                        <div className="rounded-lg border bg-gray-50 p-3 divide-y">
                                                            <div className="space-y-0.5 pb-2">
                                                                <Row label="Original Contract" value={fmt(baseContract)} />
                                                                <Row
                                                                    label="Change Orders"
                                                                    sub={changeOrders.length > 0 ? `(${changeOrders.length})` : null}
                                                                    value={`${changeOrdersTotal >= 0 ? '+' : '−'}${fmt(Math.abs(changeOrdersTotal))}`}
                                                                    color={changeOrdersTotal >= 0 ? "text-orange-700" : "text-emerald-700"}
                                                                />
                                                                <Row label="Total Job Amount" value={fmt(totalContract)} bold />
                                                            </div>
                                                            <div className="space-y-0.5 py-2">
                                                                <Row label="Amount Spent (Materials + Receipts)" value={fmt(amountSpent)} color="text-red-600" />
                                                                {payrollEst > 0 && (
                                                                    <Row label="Crew Payroll Estimate" value={fmt(payrollEst)} color="text-red-500" />
                                                                )}
                                                                {payrollActual > 0 && (
                                                                    <Row label="Crew Payroll Actual" value={fmt(payrollActual)} color="text-red-600" />
                                                                )}
                                                            </div>
                                                            <div className="pt-2">
                                                                <Row
                                                                    label="Net Profit"
                                                                    sub={payrollActual > 0 ? "(after actual payroll)" : "(payroll not yet recorded)"}
                                                                    value={fmt(netProfit)}
                                                                    color={netProfit >= 0 ? "text-green-700" : "text-red-700"}
                                                                    bold
                                                                />
                                                                <Row
                                                                    label="Profit Margin"
                                                                    value={`${margin.toFixed(1)}%`}
                                                                    color={margin >= 0 ? "text-green-700" : "text-red-700"}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </CardContent>
                                    </Card>

                                    {/* Right Column - Customer + Assigned Users */}
                                    <div className="lg:col-span-2 space-y-4">
                                        {/* Customer Details Card */}
                                        {selectedProject.customer && (
                                            <Card>
                                                <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
                                                    <CardTitle className="text-base">Customer</CardTitle>
                                                </CardHeader>
                                                <CardContent className="p-4 sm:p-6 pt-0">
                                                    <div className="text-lg font-semibold">
                                                        {selectedProject.customer.name || 'N/A'}{selectedProject.customer.last_name ? ` ${selectedProject.customer.last_name}` : ''}
                                                    </div>
                                                    <div className="mt-3 space-y-2.5">
                                                        {selectedProject.customer.email && selectedProject.customer.email !== 'N/A' && (
                                                            <div className="flex items-center gap-2">
                                                                <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                                <a href={`mailto:${selectedProject.customer.email}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate">
                                                                    {selectedProject.customer.email}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {selectedProject.customer.phone && selectedProject.customer.phone !== 'N/A' && (
                                                            <div className="flex items-center gap-2">
                                                                <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                                <a href={`tel:${selectedProject.customer.phone}`} className="text-sm hover:underline">
                                                                    {formatPhone(selectedProject.customer.phone)}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {(selectedProject.customer.address || selectedProject.customer.city) && (
                                                            <div className="flex items-center gap-2">
                                                                <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                                <span className="text-sm">
                                                                    {selectedProject.customer.address || 'N/A'}{selectedProject.customer.city || selectedProject.customer.zip_code ? `, ${[selectedProject.customer.city, selectedProject.customer.zip_code].filter(Boolean).join(' ')}` : ''}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {/* HOA + Gate Code */}
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-xs text-muted-foreground uppercase tracking-wide">HOA</span>
                                                                <Badge variant={selectedProject.customer.hoa ? "default" : "secondary"} className="text-xs">
                                                                    {selectedProject.customer.hoa ? "Yes" : "No"}
                                                                </Badge>
                                                            </div>
                                                            {selectedProject.customer.gate_code && (
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Gate Code</span>
                                                                    <span className="text-sm font-medium font-mono bg-gray-100 px-2 py-0.5 rounded">{selectedProject.customer.gate_code}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {/* Assigned Users & Crews Card */}
                                        <Card>
                                            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
                                                <CardTitle className="text-base flex items-center gap-2">
                                                    <Users className="h-4 w-4" />
                                                    Team Assignment
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent className="p-4 sm:p-6 pt-0 space-y-5">
                                                {/* Designers picker */}
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-gray-700">Assigned Designers</label>
                                                    {(selectedProject.designer_ids || []).length > 0 && (
                                                        <div className="flex flex-wrap gap-2 mb-2">
                                                            {(selectedProject.designer_ids || []).map((dId) => {
                                                                const d = designers.find(x => (x._id || x.id) === dId);
                                                                if (!d) return null;
                                                                return (
                                                                    <span key={dId} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                                                                        {d.name}
                                                                        <button
                                                                            type="button"
                                                                            onClick={async () => {
                                                                                try {
                                                                                    const projectId = selectedProject.id || selectedProject._id;
                                                                                    const next = (selectedProject.designer_ids || []).filter(id => id !== dId);
                                                                                    await projectService.update(projectId, { designer_ids: next });
                                                                                    selectedProject.designer_ids = next;
                                                                                    toast({ description: "Designer removed" });
                                                                                    await refreshSelectedProject();
                                                                                } catch (e) {
                                                                                    toast({ variant: "destructive", description: "Failed to update designers" });
                                                                                }
                                                                            }}
                                                                            className="hover:bg-purple-200 rounded-full p-0.5"
                                                                        >
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    <Select
                                                        value="__add__"
                                                        onValueChange={async (val) => {
                                                            if (val === "__add__") return;
                                                            try {
                                                                const projectId = selectedProject.id || selectedProject._id;
                                                                const next = [...(selectedProject.designer_ids || []), val];
                                                                await projectService.update(projectId, { designer_ids: next });
                                                                selectedProject.designer_ids = next;
                                                                toast({ description: "Designer added" });
                                                                await refreshSelectedProject();
                                                            } catch (e) {
                                                                toast({ variant: "destructive", description: "Failed to add designer" });
                                                            }
                                                        }}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Add a designer..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="__add__" disabled>Add a designer...</SelectItem>
                                                            {designers
                                                                .filter(d => !(selectedProject.designer_ids || []).includes(d._id || d.id))
                                                                .map((d) => (
                                                                    <SelectItem key={d._id || d.id} value={d._id || d.id}>
                                                                        {d.name}{d.specialty ? ` — ${d.specialty}` : ""}
                                                                    </SelectItem>
                                                                ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                <UserMultiSelect
                                                    selectedUserIds={selectedProject.assigned_user_ids || []}
                                                    onChange={async (userIds) => {
                                                        try {
                                                            const projectId = selectedProject.id || selectedProject._id;
                                                            await projectService.update(projectId, { assigned_user_ids: userIds });
                                                            selectedProject.assigned_user_ids = userIds;
                                                            toast({ description: "Assigned users updated successfully" });
                                                            await refreshSelectedProject();
                                                        } catch (error) {
                                                            console.error("Failed to update assigned users:", error);
                                                            toast({ variant: "destructive", description: "Failed to update assigned users" });
                                                        }
                                                    }}
                                                />
                                                {(!selectedProject.assigned_user_ids || selectedProject.assigned_user_ids.length === 0) && (
                                                    <p className="text-xs text-gray-500 -mt-3">No users assigned. Only admins can see unassigned records.</p>
                                                )}

                                                <TeamResourceMultiSelect
                                                    label="Assigned Crews & Subcontractors"
                                                    selectedCrewIds={selectedProject.assigned_crew_ids || []}
                                                    selectedSubcontractorIds={selectedProject.subcontractor_ids || []}
                                                    onChange={async ({ crewIds, subcontractorIds }) => {
                                                        try {
                                                            const projectId = selectedProject.id || selectedProject._id;
                                                            await projectService.update(projectId, {
                                                                assigned_crew_ids: crewIds,
                                                                subcontractor_ids: subcontractorIds,
                                                            });
                                                            selectedProject.assigned_crew_ids = crewIds;
                                                            selectedProject.subcontractor_ids = subcontractorIds;
                                                            toast({ description: "Team assignment updated" });
                                                            await refreshSelectedProject();
                                                        } catch (error) {
                                                            console.error("Failed to update team assignment:", error);
                                                            toast({ variant: "destructive", description: "Failed to update team assignment" });
                                                        }
                                                    }}
                                                />
                                                {((!selectedProject.assigned_crew_ids || selectedProject.assigned_crew_ids.length === 0) &&
                                                  (!selectedProject.subcontractor_ids || selectedProject.subcontractor_ids.length === 0)) && (
                                                    <p className="text-xs text-gray-500 -mt-3">No crews or subs assigned to this project.</p>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>

                                {/* Quick Actions Row */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div
                                        onClick={() => handleSendEmail(selectedProject)}
                                        className="border rounded-lg p-4 text-center hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <Mail className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                        <div className="text-sm font-medium">Send Email</div>
                                    </div>
                                    <div
                                        onClick={() => handleEditProject(selectedProject)}
                                        className="border rounded-lg p-4 text-center hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <Edit className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                        <div className="text-sm font-medium">Edit Project</div>
                                    </div>
                                    <div
                                        onClick={() => {
                                            const tabsTrigger = document.querySelector('[data-state="inactive"][value="payments"]') || document.querySelector('[value="payments"]');
                                            if (tabsTrigger) tabsTrigger.click();
                                        }}
                                        className="border rounded-lg p-4 text-center hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <CreditCard className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                        <div className="text-sm font-medium">View Payments</div>
                                    </div>
                                    <div
                                        onClick={() => {
                                            const tabsTrigger = document.querySelector('[data-state="inactive"][value="notes"]') || document.querySelector('[value="notes"]');
                                            if (tabsTrigger) tabsTrigger.click();
                                        }}
                                        className="border rounded-lg p-4 text-center hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <StickyNote className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                        <div className="text-sm font-medium">Notes / Pics</div>
                                    </div>
                                </div>
                            </TabsContent>
                            
                            {/* PAYMENTS TAB */}
                            <TabsContent value="payments" className="space-y-4 mt-0">
                                {selectedProject.project_type === "design_only" ? (
                                    /* ── DESIGN-ONLY: Single Payment ── */
                                    <>
                                        {(() => {
                                            const payment = selectedProject.payment_schedule?.[0];
                                            const designFee = selectedProject.contract_price || selectedProject.budget || 0;

                                            if (!payment && designFee <= 0) {
                                                return (
                                                    <div className="bg-white border rounded-lg p-8 text-center">
                                                        <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                                        <p className="text-sm text-muted-foreground">No design fee set. Edit the project to add a design fee.</p>
                                                    </div>
                                                );
                                            }

                                            if (!payment) {
                                                return (
                                                    <div className="border rounded-lg p-6 bg-white">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <h3 className="text-base font-semibold">Design Fee</h3>
                                                                <div className="text-3xl font-bold mt-1">${designFee.toLocaleString()}</div>
                                                                <Badge className="mt-2 py-0 text-[10px] bg-yellow-500 text-white">Pending</Badge>
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                onClick={handleGeneratePaymentSchedule}
                                                                disabled={generatingPayment}
                                                                className="bg-brand-600 hover:bg-brand-700"
                                                            >
                                                                {generatingPayment ? "Creating..." : "Create Payment"}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="border rounded-lg p-6 bg-white">
                                                    <div className="flex items-center justify-between gap-4">
                                                        <div>
                                                            <h3 className="text-base font-semibold">Design Fee</h3>
                                                            <div className="text-3xl font-bold mt-1">${(payment.amount || 0).toLocaleString()}</div>
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <Badge className={`py-0 text-[10px] ${
                                                                    payment.status === "paid" ? "bg-green-500 text-white" :
                                                                    payment.status === "overdue" ? "bg-red-500 text-white" : "bg-yellow-500 text-white"
                                                                }`}>
                                                                    {payment.status === "paid" ? "Paid" : payment.status === "overdue" ? "Overdue" : "Pending"}
                                                                </Badge>
                                                                {payment.due_date && (
                                                                    <span className="text-xs text-muted-foreground">Due: {formatDatePST(payment.due_date)}</span>
                                                                )}
                                                                {payment.paid_date && (
                                                                    <span className="text-xs text-green-600">Paid: {formatDatePST(payment.paid_date)}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {payment.status === "paid" && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    title={payment.receipt_sent_at ? `Receipt sent ${new Date(payment.receipt_sent_at).toLocaleString()} — click to send again` : "Send Receipt"}
                                                                    onClick={() => handleOpenSendReceipt(payment)}
                                                                    className="h-8 w-8 p-0"
                                                                >
                                                                    <Receipt className={`h-4 w-4 ${payment.receipt_sent_at ? 'text-green-600' : ''}`} />
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => {
                                                                    setEditingPayment({
                                                                        ...payment,
                                                                        due_date: payment.due_date || "",
                                                                        paid_date: payment.paid_date || ""
                                                                    });
                                                                    setIsPaymentDialogOpen(true);
                                                                }}
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            {payment.is_extra && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    title="Delete extra payment"
                                                                    onClick={() => handleDeleteExtraPayment(payment)}
                                                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    /* ── CONSTRUCTION: Payment Schedule ── */
                                    <>
                                {/* Payment Summary Cards */}
                                {selectedProject.payment_schedule && selectedProject.payment_schedule.length > 0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Total Contract</div>
                                            <div className="text-2xl sm:text-3xl font-bold mt-1">
                                                ${(selectedProject.total_job_cost || selectedProject.contract_price || 0).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Paid to Date</div>
                                            <div className="text-2xl sm:text-3xl font-bold mt-1 text-green-600">
                                                ${selectedProject.payment_schedule
                                                    .filter(p => p.status === 'paid')
                                                    .reduce((sum, p) => sum + (p.amount || 0), 0)
                                                    .toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="border rounded-lg p-4 bg-white">
                                            <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Remaining</div>
                                            {(() => {
                                                const totalContract = selectedProject.total_job_cost || selectedProject.contract_price || 0;
                                                const paidAmount = selectedProject.payment_schedule.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);
                                                const remaining = totalContract - paidAmount;
                                                const progressPercent = totalContract > 0 ? (paidAmount / totalContract) * 100 : 0;
                                                return (
                                                    <>
                                                        <div className={`text-2xl sm:text-3xl font-bold mt-1 ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                            ${remaining.toLocaleString()}
                                                        </div>
                                                        <Progress value={progressPercent} className="h-1.5 mt-2" />
                                                        <div className="text-xs text-muted-foreground mt-1">{Math.round(progressPercent)}% collected</div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {/* Payment Schedule Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <h3 className="text-base font-semibold flex items-center gap-2">
                                        <CreditCard className="h-4 w-4" />
                                        Payment Schedule
                                    </h3>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleInsertExtraPayment}
                                        disabled={insertingPayment}
                                        className="text-brand-600 hover:text-brand-700 h-10"
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        {insertingPayment ? "Inserting..." : "Insert Extra Payment"}
                                    </Button>
                                </div>

                                {/* Payment Items */}
                                {selectedProject.payment_schedule && selectedProject.payment_schedule.length > 0 ? (
                                    <div className="space-y-2">
                                        {selectedProject.payment_schedule.map((payment, index) => (
                                            <div key={index} className="border-b last:border-b-0 p-3">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                                        <span className="text-sm">{payment.description}</span>
                                                        {payment.override && (
                                                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 py-0">
                                                                Override
                                                            </Badge>
                                                        )}
                                                        <Badge className={`py-0 text-[10px] ${
                                                            payment.status === "paid" ? "bg-green-500 text-white" :
                                                            payment.status === "overdue" ? "bg-red-500 text-white" : "bg-yellow-500 text-white"
                                                        }`}>
                                                            {payment.status === "paid" ? "Paid" : payment.status === "overdue" ? "Overdue" : "Pending"}
                                                        </Badge>
                                                        {payment.due_date && (
                                                            <span className="text-xs text-muted-foreground">Due: {formatDatePST(payment.due_date)}</span>
                                                        )}
                                                        {payment.paid_date && (
                                                            <span className="text-xs text-green-600">Paid: {formatDatePST(payment.paid_date)}</span>
                                                        )}
                                                        {payment.payment_method && (
                                                            <span className="text-xs text-blue-600 capitalize">
                                                                {payment.payment_method.replace('_', ' ')}
                                                                {payment.payment_reference ? ` · ${payment.payment_reference}` : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        <span className="text-base font-semibold">${payment.amount.toLocaleString()}</span>
                                                        {payment.status === "paid" && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                title={payment.receipt_sent_at ? `Receipt sent ${new Date(payment.receipt_sent_at).toLocaleString()} — click to send again` : "Send Receipt"}
                                                                onClick={() => handleOpenSendReceipt(payment)}
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                <Receipt className={`h-3.5 w-3.5 ${payment.receipt_sent_at ? 'text-green-600' : ''}`} />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                setEditingPayment({
                                                                    ...payment,
                                                                    due_date: payment.due_date || "",
                                                                    paid_date: payment.paid_date || ""
                                                                });
                                                                setIsPaymentDialogOpen(true);
                                                            }}
                                                            className="h-8 w-8 p-0"
                                                        >
                                                            <Edit className="h-3.5 w-3.5" />
                                                        </Button>
                                                        {payment.is_extra && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                title="Delete extra payment"
                                                                onClick={() => handleDeleteExtraPayment(payment)}
                                                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-white border rounded-lg p-8 text-center">
                                        <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">No payment schedule available.</p>
                                    </div>
                                )}
                                    </>
                                )}

                                {/* Change Orders Section */}
                                <div className="pt-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                        <h3 className="text-base font-semibold flex items-center gap-2">
                                            <FileText className="h-4 w-4" />
                                            Change Orders
                                        </h3>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setIsChangeOrderDialogOpen(true)}
                                                className="h-10"
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Log Entry
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => setIsCreateChangeOrderOpen(true)}
                                                className="bg-brand-600 hover:bg-brand-700 h-10"
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Create Change Order
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Manual Log Entries */}
                                    {selectedProject.project_type === "construction" && (
                                        <div className="max-h-[300px] overflow-y-auto mb-4">
                                            {selectedProject.change_orders && selectedProject.change_orders.length > 0 ? (
                                                <>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2">Manual Log Entries</div>
                                                    <div className="space-y-0">
                                                        {selectedProject.change_orders.map((changeOrder, index) => (
                                                            <div key={index} className="border-b last:border-b-0 p-3">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                        <span className="text-sm">{changeOrder.scope_of_work}</span>
                                                                        <span className="text-xs text-muted-foreground">{formatDatePST(changeOrder.date)}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                        <span className="text-sm font-semibold text-green-600">+${changeOrder.amount.toLocaleString()}</span>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleEditChangeOrder(changeOrder, index);
                                                                            }}
                                                                            className="h-8 w-8 p-0"
                                                                        >
                                                                            <Edit className="w-3.5 h-3.5" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteChangeOrder(index);
                                                                            }}
                                                                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5" />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="bg-white border rounded-lg p-4 text-center">
                                                    <p className="text-sm text-muted-foreground">No manual change order entries.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Change Order Documents */}
                                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">Change Order Documents</div>

                                    {loadingChangeOrders ? (
                                        <div className="bg-white border rounded-lg p-6 text-center">
                                            <p className="text-sm text-muted-foreground">Loading change orders...</p>
                                        </div>
                                    ) : changeOrders.length === 0 ? (
                                        <div className="bg-white border rounded-lg p-8 text-center">
                                            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-sm text-muted-foreground">No change order documents created yet.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-0">
                                            {changeOrders.map(co => (
                                                <div key={co.id} className="border-b last:border-b-0 p-3">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                                            <span className="text-sm truncate">
                                                                {co.change_order_data?.[0]?.description || 'Change Order'}
                                                            </span>
                                                            <Badge className={`py-0 text-[10px] ${co.status === 'Signed' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`}>
                                                                {co.status}
                                                            </Badge>
                                                            <span className="text-xs text-muted-foreground">
                                                                {new Date(co.created_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-base font-semibold">{formatCurrency(co.total)}</span>
                                                                <Select
                                                                    value={co.status}
                                                                    onValueChange={(newStatus) => handleStatusChange(co.id, newStatus)}
                                                                >
                                                                    <SelectTrigger className="w-[100px] h-8 text-xs">
                                                                        <SelectValue placeholder="Status" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="Unsigned">Unsigned</SelectItem>
                                                                        <SelectItem value="Signed">Signed</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleViewChangeOrder(co)}
                                                                    className="h-8 w-8 p-0"
                                                                >
                                                                    <Eye className="w-3.5 h-3.5" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteChangeOrderDocument(co.id)}
                                                                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                            
                            {/* MATERIALS TAB */}
                            <TabsContent value="materials" className="space-y-4 mt-0">
                                {/* Summary Card */}
                                {selectedProject.materials && selectedProject.materials.length > 0 && (
                                    <div className="border rounded-lg p-4 bg-white">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Total Materials Cost</div>
                                        <div className="text-2xl sm:text-3xl font-bold mt-1 text-red-600">
                                            ${selectedProject.materials.reduce((sum, m) => sum + (m.cost || 0), 0).toLocaleString()}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{selectedProject.materials.length} item{selectedProject.materials.length !== 1 ? 's' : ''}</div>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <h3 className="text-base font-semibold flex items-center gap-2">
                                        <Wrench className="h-4 w-4" />
                                        Materials List
                                    </h3>
                                    <Button
                                        size="sm"
                                        onClick={() => setIsMaterialDialogOpen(true)}
                                        className="bg-brand-600 hover:bg-brand-700 h-10"
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Material
                                    </Button>
                                </div>

                                {/* Material Items */}
                                {selectedProject.materials && selectedProject.materials.length > 0 ? (
                                    <>
                                        {/* Desktop Table */}
                                        <div className="hidden md:block overflow-x-auto border rounded-lg">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <SortHeader label="Item Name" field="name" sort={materialSort} onSort={handleMaterialSort} />
                                                        <SortHeader label="Vendor" field="company_name" sort={materialSort} onSort={handleMaterialSort} />
                                                        <TableHead className="cursor-pointer select-none hover:bg-muted/50 text-xs text-right" onClick={() => handleMaterialSort('cost')}>
                                                            <span className="flex items-center justify-end gap-1">
                                                                Cost
                                                                {materialSort.field === 'cost' ? (materialSort.direction === 'asc' ? ' \u25B2' : ' \u25BC') : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                                                            </span>
                                                        </TableHead>
                                                        <SortHeader label="Invoice #" field="invoice_number" sort={materialSort} onSort={handleMaterialSort} />
                                                        <SortHeader label="Date" field="date" sort={materialSort} onSort={handleMaterialSort} />
                                                        <TableHead className="text-xs w-[80px]">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {sortItems(selectedProject.materials, materialSort).map((material, sortedIndex) => {
                                                        const originalIndex = selectedProject.materials.findIndex(m => m === material);
                                                        return (
                                                            <TableRow key={sortedIndex}>
                                                                <TableCell className="text-sm font-medium">{material.name}</TableCell>
                                                                <TableCell className="text-sm text-brand-600">{material.company_name || '-'}</TableCell>
                                                                <TableCell className={`text-sm text-right font-medium ${material.cost < 0 ? 'text-red-600' : ''}`}>${material.cost.toLocaleString()}</TableCell>
                                                                <TableCell className="text-sm text-blue-600">{material.invoice_number || '-'}</TableCell>
                                                                <TableCell className="text-sm">{material.date ? material.date.split('T')[0] : '-'}</TableCell>
                                                                <TableCell>
                                                                    <div className="flex items-center gap-0.5">
                                                                        <Button variant="ghost" size="sm" onClick={() => handleEditMaterial(material, originalIndex)} className="h-7 w-7 p-0">
                                                                            <Edit className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(originalIndex)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {/* Mobile Compact List */}
                                        <div className="md:hidden space-y-0 border rounded-lg">
                                            {sortItems(selectedProject.materials, materialSort).map((material, sortedIndex) => {
                                                const originalIndex = selectedProject.materials.findIndex(m => m === material);
                                                return (
                                                    <div key={sortedIndex} className="border-b last:border-b-0 p-3">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium truncate">{material.name}</div>
                                                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                                                    {material.company_name && <span className="text-brand-600">{material.company_name}</span>}
                                                                    {material.invoice_number && <span>#{material.invoice_number}</span>}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <span className={`text-sm font-semibold ${material.cost < 0 ? 'text-red-600' : ''}`}>${material.cost.toLocaleString()}</span>
                                                                <Button variant="ghost" size="sm" onClick={() => handleEditMaterial(material, originalIndex)} className="h-7 w-7 p-0">
                                                                    <Edit className="h-3.5 w-3.5" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(originalIndex)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                                                                    <Trash2 className="h-3.5 w-3.5" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <div className="bg-white border rounded-lg p-8 text-center">
                                        <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">No materials added yet.</p>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setIsMaterialDialogOpen(true)}
                                            className="mt-3 h-10"
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add First Material
                                        </Button>
                                    </div>
                                )}
                            </TabsContent>
                            
                            {/* RECEIPTS TAB */}
                            <TabsContent value="receipts" className="space-y-4 mt-0">
                                {/* Summary Card */}
                                {selectedProject.receipts && selectedProject.receipts.length > 0 && (
                                    <div className="border rounded-lg p-4 bg-white">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Total Receipts</div>
                                        <div className="text-2xl sm:text-3xl font-bold mt-1 text-red-600">
                                            ${selectedProject.receipts.reduce((sum, r) => sum + (r.cost || 0), 0).toLocaleString()}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{selectedProject.receipts.length} receipt{selectedProject.receipts.length !== 1 ? 's' : ''}</div>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <h3 className="text-base font-semibold flex items-center gap-2">
                                        <Receipt className="h-4 w-4" />
                                        Receipts
                                    </h3>
                                    <Button
                                        size="sm"
                                        onClick={() => setIsReceiptDialogOpen(true)}
                                        className="bg-brand-600 hover:bg-brand-700 h-10"
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Receipt
                                    </Button>
                                </div>

                                {/* Receipt Items */}
                                {selectedProject.receipts && selectedProject.receipts.length > 0 ? (
                                    <>
                                        {/* Desktop Table */}
                                        <div className="hidden md:block overflow-x-auto border rounded-lg">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <SortHeader label="Item / Description" field="item" sort={receiptSort} onSort={handleReceiptSort} />
                                                        <SortHeader label="Vendor" field="company_name" sort={receiptSort} onSort={handleReceiptSort} />
                                                        <TableHead className="cursor-pointer select-none hover:bg-muted/50 text-xs text-right" onClick={() => handleReceiptSort('cost')}>
                                                            <span className="flex items-center justify-end gap-1">
                                                                Cost
                                                                {receiptSort.field === 'cost' ? (receiptSort.direction === 'asc' ? ' \u25B2' : ' \u25BC') : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                                                            </span>
                                                        </TableHead>
                                                        <SortHeader label="Invoice #" field="invoice_number" sort={receiptSort} onSort={handleReceiptSort} />
                                                        <SortHeader label="Date" field="date" sort={receiptSort} onSort={handleReceiptSort} />
                                                        <TableHead className="text-xs w-[80px]">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {sortItems(selectedProject.receipts, receiptSort).map((receipt, sortedIndex) => {
                                                        const originalIndex = selectedProject.receipts.findIndex(r => r === receipt);
                                                        return (
                                                            <TableRow key={sortedIndex}>
                                                                <TableCell className="text-sm font-medium">{receipt.item}</TableCell>
                                                                <TableCell className="text-sm text-brand-600">{receipt.company_name || '-'}</TableCell>
                                                                <TableCell className={`text-sm text-right font-medium ${receipt.cost < 0 ? 'text-red-600' : ''}`}>${receipt.cost.toLocaleString()}</TableCell>
                                                                <TableCell className="text-sm text-blue-600">{receipt.invoice_number || '-'}</TableCell>
                                                                <TableCell className="text-sm">{receipt.date ? receipt.date.split('T')[0] : '-'}</TableCell>
                                                                <TableCell>
                                                                    <div className="flex items-center gap-0.5">
                                                                        <Button variant="ghost" size="sm" onClick={() => handleEditReceipt(receipt, originalIndex)} className="h-7 w-7 p-0">
                                                                            <Edit className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteReceipt(originalIndex)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {/* Mobile Compact List */}
                                        <div className="md:hidden space-y-0 border rounded-lg">
                                            {sortItems(selectedProject.receipts, receiptSort).map((receipt, sortedIndex) => {
                                                const originalIndex = selectedProject.receipts.findIndex(r => r === receipt);
                                                return (
                                                    <div key={sortedIndex} className="border-b last:border-b-0 p-3">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium truncate">{receipt.item}</div>
                                                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                                                    {receipt.company_name && <span className="text-brand-600">{receipt.company_name}</span>}
                                                                    {receipt.invoice_number && <span>#{receipt.invoice_number}</span>}
                                                                    {receipt.date && <span>{receipt.date.split('T')[0]}</span>}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <span className={`text-sm font-semibold ${receipt.cost < 0 ? 'text-red-600' : ''}`}>${receipt.cost.toLocaleString()}</span>
                                                                <Button variant="ghost" size="sm" onClick={() => handleEditReceipt(receipt, originalIndex)} className="h-7 w-7 p-0">
                                                                    <Edit className="h-3.5 w-3.5" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleDeleteReceipt(originalIndex)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                                                                    <Trash2 className="h-3.5 w-3.5" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <div className="bg-white border rounded-lg p-8 text-center">
                                        <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">No receipts added yet.</p>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setIsReceiptDialogOpen(true)}
                                            className="mt-3 h-10"
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add First Receipt
                                        </Button>
                                    </div>
                                )}
                            </TabsContent>

                            {/* EVENTS & APPOINTMENTS TAB */}
                            <TabsContent value="events" className="space-y-4 mt-0">
                                {/* Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <h3 className="text-base font-semibold flex items-center gap-2">
                                        <Calendar className="h-4 w-4" />
                                        Project Events & Appointments
                                    </h3>
                                    <Button onClick={handleAddEvent} size="sm" className="bg-brand-600 hover:bg-brand-700 h-10">
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Event
                                    </Button>
                                </div>

                                {/* Timeline-style Events */}
                                {selectedProject?.activity_log && selectedProject.activity_log.length > 0 ? (
                                    <div className="relative">
                                        {/* Vertical timeline connector */}
                                        <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-200"></div>
                                        <div className="space-y-3">
                                            {selectedProject.activity_log.slice().reverse().map((event, reversedIndex) => {
                                                const originalIndex = selectedProject.activity_log.length - 1 - reversedIndex;
                                                const activity = event.activity || '';
                                                const isAppointment = activity.startsWith('\u{1F4C5}') || activity.startsWith('[APT]');
                                                const isEvent = activity.startsWith('\u{1F4CC}') || activity.startsWith('[EVENT]');

                                                if (!isAppointment && !isEvent) return null;

                                                let displayText = activity;
                                                if (activity.startsWith('[APT]')) {
                                                    displayText = activity.replace('[APT] ', '');
                                                } else if (activity.startsWith('[EVENT]')) {
                                                    displayText = activity.replace('[EVENT] ', '');
                                                } else if (activity.startsWith('\u{1F4C5}')) {
                                                    displayText = activity.replace('\u{1F4C5} ', '');
                                                } else if (activity.startsWith('\u{1F4CC}')) {
                                                    displayText = activity.replace('\u{1F4CC} ', '');
                                                }

                                                return (
                                                <div key={originalIndex} className="relative flex items-start gap-4 pl-10">
                                                    {/* Timeline dot */}
                                                    <div className={`absolute left-[9px] top-5 w-3 h-3 rounded-full border-2 border-white z-10 ${isAppointment ? 'bg-blue-500' : 'bg-purple-500'}`}></div>
                                                    {/* Event card */}
                                                    <div className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow flex-1">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-sm font-semibold">{displayText}</span>
                                                                    <Badge className={isAppointment ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' : 'bg-purple-100 text-purple-700 hover:bg-purple-100'}>
                                                                        {isAppointment ? 'Appointment' : 'Event'}
                                                                    </Badge>
                                                                </div>
                                                                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                                                    <span className="flex items-center gap-1">
                                                                        <Calendar className="h-3 w-3" />
                                                                        {new Date(event.date).toLocaleString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric',
                                                                            hour: 'numeric',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </span>
                                                                    <span>Added by {event.user}</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleEditEvent(event, originalIndex)}
                                                                    className="h-10 w-10 p-0"
                                                                >
                                                                    <Edit className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteEvent(originalIndex)}
                                                                    className="h-10 w-10 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white border rounded-lg p-8 text-center">
                                        <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">No project events or appointments added yet</p>
                                        <p className="text-xs text-muted-foreground mt-1">Click &quot;Add Event&quot; to get started</p>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleAddEvent}
                                            className="mt-3 h-10"
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add First Event
                                        </Button>
                                    </div>
                                )}

                                {/* Customer Appointments Section */}
                                <div className="pt-2">
                                    <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                                        <Clock className="h-4 w-4" />
                                        Customer Appointments
                                        <Badge variant="secondary" className="text-xs">From Customer</Badge>
                                    </h3>

                                    {selectedProject?.customer?.appointments && selectedProject.customer.appointments.length > 0 ? (
                                        <div className="space-y-3">
                                            {selectedProject.customer.appointments.slice().reverse().map((appointment, index) => (
                                                <div key={index} className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-semibold">{appointment.title}</span>
                                                                <Badge className={
                                                                    appointment.status === 'completed' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                                                                    appointment.status === 'cancelled' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                                                                    appointment.status === 'no_show' ? 'bg-gray-100 text-gray-700 hover:bg-gray-100' :
                                                                    'bg-blue-100 text-blue-700 hover:bg-blue-100'
                                                                }>
                                                                    {appointment.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                                                                    {appointment.status === 'cancelled' && <XCircle className="h-3 w-3 mr-1" />}
                                                                    {appointment.status === 'no_show' && <MinusCircle className="h-3 w-3 mr-1" />}
                                                                    {appointment.status === 'scheduled' && <Calendar className="h-3 w-3 mr-1" />}
                                                                    {appointment.status}
                                                                </Badge>
                                                            </div>
                                                            {appointment.description && (
                                                                <p className="text-xs text-muted-foreground mt-2">{appointment.description}</p>
                                                            )}
                                                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                                <span className="flex items-center gap-1">
                                                                    <Calendar className="h-3 w-3" />
                                                                    {new Date(appointment.scheduled_date).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'short',
                                                                        day: 'numeric'
                                                                    })}
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="h-3 w-3" />
                                                                    {appointment.scheduled_time}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground mt-2 italic">
                                                                Shared from customer record - Edit in customer view
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="bg-white border rounded-lg p-8 text-center">
                                            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-sm text-muted-foreground">No customer appointments</p>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            {/* TIMELINE TAB */}
                            <TabsContent value="timeline" className="space-y-4 mt-0">
                                <h3 className="text-base font-semibold flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Project Timeline
                                </h3>

                                {loadingEmailHistory ? (
                                    <div className="bg-white border rounded-lg p-6 text-center">
                                        <p className="text-sm text-muted-foreground">Loading timeline...</p>
                                    </div>
                                ) : sortedTimelineEvents.length === 0 ? (
                                    <div className="bg-white border rounded-lg p-8 text-center">
                                        <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">No timeline events found.</p>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        {/* Vertical timeline connector */}
                                        <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-200"></div>
                                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                            {sortedTimelineEvents.map((event, index) => (
                                                <div key={index} className="relative flex items-start gap-4 pl-10">
                                                    {/* Timeline dot */}
                                                    <div className={`absolute left-[9px] top-5 w-3 h-3 rounded-full border-2 border-white z-10 ${event.color}`}></div>
                                                    {/* Event card */}
                                                    <div className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow flex-1">
                                                        <div className="text-sm font-semibold">{event.title}</div>
                                                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                            <Calendar className="h-3 w-3" />
                                                            {formatDatePST(event.date)}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground mt-1">{event.description}</div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Expected Completion */}
                                            <div className="relative flex items-start gap-4 pl-10">
                                                <div className="absolute left-[9px] top-5 w-3 h-3 rounded-full border-2 border-white z-10 bg-gray-300"></div>
                                                <div className="bg-white border rounded-lg p-4 sm:p-5 border-dashed flex-1">
                                                    <div className="text-sm font-semibold text-muted-foreground">Expected Completion</div>
                                                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                        <Calendar className="h-3 w-3" />
                                                        {selectedProject.end_date ? formatDatePST(selectedProject.end_date) : 'Not set'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </TabsContent>
                            
                            {/* TEAM TAB */}
                            <TabsContent value="team" className="space-y-6 mt-0">
                                {/* Crew Payroll: estimated vs actual */}
                                {(() => {
                                    const est = parseFloat(selectedProject.crew_payroll_estimate || 0);
                                    const act = parseFloat(selectedProject.crew_payroll_actual || 0);
                                    const diff = est - act; // positive = under budget, negative = over
                                    const projectId = selectedProject.id || selectedProject._id;

                                    const savePayroll = async (field, valueStr) => {
                                        const value = parseFloat(valueStr || 0) || 0;
                                        try {
                                            await projectService.update(projectId, { [field]: value });
                                            selectedProject[field] = value;
                                            await refreshSelectedProject();
                                        } catch (err) {
                                            console.error("Failed to save payroll:", err);
                                            toast({ variant: "destructive", description: "Failed to save payroll" });
                                        }
                                    };

                                    return (
                                        <div>
                                            <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                                                <DollarSign className="h-4 w-4" />
                                                Crew Payroll
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div className="bg-white border rounded-lg p-4">
                                                    <Label className="text-xs uppercase text-gray-500">Estimated</Label>
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <span className="text-gray-500">$</span>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            defaultValue={est || ""}
                                                            placeholder="0.00"
                                                            className="border-0 px-0 text-lg font-semibold focus-visible:ring-0"
                                                            onBlur={(e) => {
                                                                const v = parseFloat(e.target.value || 0) || 0;
                                                                if (v !== est) savePayroll("crew_payroll_estimate", e.target.value);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="bg-white border rounded-lg p-4">
                                                    <Label className="text-xs uppercase text-gray-500">Actual</Label>
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <span className="text-gray-500">$</span>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            defaultValue={act || ""}
                                                            placeholder="0.00"
                                                            className="border-0 px-0 text-lg font-semibold focus-visible:ring-0"
                                                            onBlur={(e) => {
                                                                const v = parseFloat(e.target.value || 0) || 0;
                                                                if (v !== act) savePayroll("crew_payroll_actual", e.target.value);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {est > 0 && act > 0 && (
                                                <div className={`mt-3 rounded-lg border p-3 flex items-center justify-between ${
                                                    diff >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                                                }`}>
                                                    <span className="text-sm text-gray-700">
                                                        {diff >= 0 ? 'Under estimate by' : 'Over estimate by'}
                                                    </span>
                                                    <span className={`text-base font-semibold ${
                                                        diff >= 0 ? 'text-emerald-700' : 'text-red-700'
                                                    }`}>
                                                        ${Math.abs(diff).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Internal Team Members */}
                                <div>
                                    <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                                        <Users className="h-4 w-4" />
                                        Internal Team
                                    </h3>
                                    {selectedProject.team && selectedProject.team.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {selectedProject.team.map((member, index) => (
                                                <div key={index} className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <Users className="h-5 w-5 text-brand-600" />
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-semibold">{member}</div>
                                                            <Badge variant="outline" className="text-xs mt-1">Team Member</Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="bg-white border rounded-lg p-8 text-center">
                                            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-sm text-muted-foreground">No internal team members assigned</p>
                                        </div>
                                    )}
                                </div>

                                {/* Designers */}
                                <div>
                                    <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                                        <Palette className="h-4 w-4" />
                                        Designers
                                    </h3>
                                    {selectedProject.designer_ids && selectedProject.designer_ids.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {selectedProject.designer_ids.map((designerId) => {
                                                const designer = designers.find(d => (d._id || d.id) === designerId);
                                                return designer ? (
                                                    <div key={designerId} className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                                <Palette className="h-5 w-5 text-purple-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-semibold">{designer.name}</div>
                                                                <Badge variant="outline" className="text-xs mt-1">
                                                                    {designer.specialty ? designer.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Designer'}
                                                                </Badge>
                                                                <div className="mt-3 space-y-1.5">
                                                                    {designer.email && (
                                                                        <div className="flex items-center gap-2">
                                                                            <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                                            <a href={`mailto:${designer.email}`} className="text-xs text-blue-600 hover:underline truncate">{designer.email}</a>
                                                                        </div>
                                                                    )}
                                                                    {designer.phone && (
                                                                        <div className="flex items-center gap-2">
                                                                            <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                                            <a href={`tel:${designer.phone}`} className="text-xs hover:underline">{formatPhone(designer.phone)}</a>
                                                                        </div>
                                                                    )}
                                                                    {designer.rate_per_hour && (
                                                                        <div className="flex items-center gap-2">
                                                                            <DollarSign className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                                            <span className="text-xs font-medium text-green-600">${designer.rate_per_hour}/hr</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : null;
                                            })}
                                        </div>
                                    ) : (
                                        <div className="bg-white border rounded-lg p-8 text-center">
                                            <Palette className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-sm text-muted-foreground">No designers assigned</p>
                                        </div>
                                    )}
                                </div>

                                {/* Subcontractors removed — work crews are now assigned from the Overview tab */}
                            </TabsContent>

                            {/* PRODUCTION TAB */}
                            <TabsContent value="production" className="space-y-4 mt-0">
                                {!selectedProject?.in_production ? (
                                    <div className="bg-white border rounded-lg p-8 text-center">
                                        <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                        <h3 className="font-semibold mb-2">Not in Production Workflow</h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Add this project to the production kanban to track install stages, assign crews, and monitor progress.
                                        </p>
                                        <Button onClick={handleStartProduction} disabled={productionBusy}>
                                            {productionBusy ? 'Adding...' : 'Add to Production'}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between bg-white border rounded-lg p-4">
                                            <div>
                                                <div className="text-sm text-muted-foreground">Current Stage</div>
                                                {currentProductionStage ? (
                                                    <Badge
                                                        className="text-white mt-1"
                                                        style={{ backgroundColor: currentProductionStage.color || '#64748b' }}
                                                    >
                                                        {currentProductionStage.label}
                                                    </Badge>
                                                ) : (
                                                    <div className="text-sm">-</div>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="outline" onClick={handleSyncProductionStages} disabled={productionBusy}>
                                                    Sync Stages
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-red-600" onClick={handleStopProduction} disabled={productionBusy}>
                                                    Remove from Production
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="bg-white border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="w-[40px]"></TableHead>
                                                        <TableHead>Stage</TableHead>
                                                        <TableHead className="w-[140px]">Status</TableHead>
                                                        <TableHead className="w-[140px]">Crew</TableHead>
                                                        <TableHead className="w-[130px]">Start</TableHead>
                                                        <TableHead className="w-[130px]">Complete</TableHead>
                                                        <TableHead>Notes</TableHead>
                                                        <TableHead className="w-[80px]"></TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {/* Show only stages that exist on this project */}
                                                    {(selectedProject?.production_stages || []).map((progress, stageIdx) => {
                                                        const stageDef = productionStages.find(s => s.value === progress.stage_value) || {};
                                                        const isOptional = stageDef.is_optional || false;
                                                        return (
                                                            <TableRow
                                                                key={progress.stage_value}
                                                                draggable={isOptional}
                                                                onDragStart={isOptional ? (e) => handleStageDragStart(e, progress.stage_value) : undefined}
                                                                onDragOver={(e) => handleStageDragOver(e, stageIdx)}
                                                                onDrop={(e) => handleStageDrop(e, stageIdx)}
                                                                onDragEnd={handleStageDragEnd}
                                                                className={`${dragOverIdx === stageIdx ? 'border-t-2 border-blue-500' : ''} ${draggedStageValue === progress.stage_value ? 'opacity-40' : ''}`}
                                                            >
                                                                <TableCell>
                                                                    <div
                                                                        className="w-4 h-4 rounded"
                                                                        style={{ backgroundColor: stageDef.color || '#64748b' }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell className="font-medium">
                                                                    {stageDef.label || progress.stage_value}
                                                                    {isOptional && <span className="ml-1 text-[10px] text-amber-600">(optional)</span>}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Select
                                                                        value={progress.status || 'not_started'}
                                                                        onValueChange={(value) =>
                                                                            handleUpdateProductionStage(progress.stage_value, { status: value })
                                                                        }
                                                                    >
                                                                        <SelectTrigger className="h-8 text-xs">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="not_started">Not Started</SelectItem>
                                                                            <SelectItem value="in_progress">In Progress</SelectItem>
                                                                            <SelectItem value="complete">Complete</SelectItem>
                                                                            <SelectItem value="skipped">Skipped</SelectItem>
                                                                            <SelectItem value="blocked">Blocked</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Select
                                                                        value={
                                                                            progress.subcontractor_id
                                                                                ? `sub:${progress.subcontractor_id}`
                                                                                : progress.crew_id
                                                                                ? `crew:${progress.crew_id}`
                                                                                : '__none__'
                                                                        }
                                                                        onValueChange={(value) => {
                                                                            let updates = { crew_id: null, subcontractor_id: null };
                                                                            if (value !== '__none__') {
                                                                                if (value.startsWith('crew:')) updates.crew_id = value.slice(5);
                                                                                else if (value.startsWith('sub:')) updates.subcontractor_id = value.slice(4);
                                                                            }
                                                                            handleUpdateProductionStage(progress.stage_value, updates);
                                                                        }}
                                                                    >
                                                                        <SelectTrigger className="h-8 text-xs">
                                                                            <SelectValue placeholder="—" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="__none__">— None —</SelectItem>
                                                                            {crews.length > 0 && (
                                                                                <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50">CREWS</div>
                                                                            )}
                                                                            {crews.map((crew) => (
                                                                                <SelectItem key={`crew-${crew.id}`} value={`crew:${crew.id}`}>
                                                                                    {crew.name}
                                                                                </SelectItem>
                                                                            ))}
                                                                            {subcontractors.length > 0 && (
                                                                                <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 mt-1">SUBCONTRACTORS</div>
                                                                            )}
                                                                            {subcontractors.map((sub) => (
                                                                                <SelectItem key={`sub-${sub._id || sub.id}`} value={`sub:${sub._id || sub.id}`}>
                                                                                    {sub.name}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Input
                                                                        type="date"
                                                                        className="h-8 text-xs"
                                                                        defaultValue={progress.start_date || ''}
                                                                        onBlur={(e) => {
                                                                            const val = e.target.value;
                                                                            if (val !== (progress.start_date || '')) {
                                                                                handleUpdateProductionStage(progress.stage_value, { start_date: val || null });
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Input
                                                                        type="date"
                                                                        className="h-8 text-xs"
                                                                        defaultValue={progress.completion_date || ''}
                                                                        onBlur={(e) => {
                                                                            const val = e.target.value;
                                                                            if (val !== (progress.completion_date || '')) {
                                                                                handleUpdateProductionStage(progress.stage_value, { completion_date: val || null });
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Input
                                                                        className="h-8 text-xs"
                                                                        defaultValue={progress.notes || ''}
                                                                        placeholder="Add notes..."
                                                                        onBlur={(e) => {
                                                                            const val = e.target.value;
                                                                            if (val !== (progress.notes || '')) {
                                                                                handleUpdateProductionStage(progress.stage_value, { notes: val || null });
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {isOptional ? (
                                                                        <div className="flex items-center gap-0.5">
                                                                            <div className="cursor-grab active:cursor-grabbing" title="Drag to reorder">
                                                                                <GripVertical className="h-4 w-4 text-gray-400" />
                                                                            </div>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                                                                onClick={() => handleRemoveOptionalStage(progress.stage_value)}
                                                                                disabled={productionBusy}
                                                                                title="Remove optional stage"
                                                                            >
                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                        </div>
                                                                    ) : null}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {/* Add optional stages */}
                                        {(() => {
                                            const projectStageValues = (selectedProject?.production_stages || []).map(s => s.stage_value);
                                            const availableOptional = productionStages.filter(
                                                s => s.is_active && s.is_optional && !projectStageValues.includes(s.value)
                                            );
                                            if (availableOptional.length === 0) return null;
                                            return (
                                                <div className="flex items-center gap-2 bg-white border rounded-lg p-3">
                                                    <span className="text-sm text-muted-foreground">Add optional stage:</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {availableOptional.map(stage => (
                                                            <Button
                                                                key={stage.value}
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 text-xs"
                                                                onClick={() => handleAddOptionalStage(stage.value)}
                                                                disabled={productionBusy}
                                                            >
                                                                <Plus className="mr-1 h-3 w-3" />
                                                                {stage.label}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </TabsContent>

                            {/* NOTES/PICTURES TAB */}
                            <TabsContent value="callbacks" className="space-y-4 mt-0">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-base font-semibold flex items-center gap-2">
                                        <PhoneIncoming className="h-4 w-4" />
                                        Callbacks
                                    </h3>
                                    <Button
                                        onClick={() => setShowCallbackForm(!showCallbackForm)}
                                        className="bg-brand-600 hover:bg-brand-700 h-9"
                                        size="sm"
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Log Callback
                                    </Button>
                                </div>

                                {/* Add Callback Form */}
                                {showCallbackForm && (
                                    <div className="bg-white border rounded-lg p-4 sm:p-5 space-y-3">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-sm font-medium">Reported By</Label>
                                                <Input
                                                    placeholder="Customer name / who called"
                                                    value={newCallback.caller_name}
                                                    onChange={(e) => setNewCallback({...newCallback, caller_name: e.target.value})}
                                                    className="mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm font-medium">Reported Via</Label>
                                                <Select
                                                    value={newCallback.contact_method}
                                                    onValueChange={(value) => setNewCallback({...newCallback, contact_method: value})}
                                                >
                                                    <SelectTrigger className="mt-1">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="phone">Phone</SelectItem>
                                                        <SelectItem value="email">Email</SelectItem>
                                                        <SelectItem value="text">Text</SelectItem>
                                                        <SelectItem value="in_person">In Person</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium">Urgency</Label>
                                            <Select
                                                value={newCallback.urgency}
                                                onValueChange={(value) => setNewCallback({...newCallback, urgency: value})}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="low">Low</SelectItem>
                                                    <SelectItem value="normal">Normal</SelectItem>
                                                    <SelectItem value="high">High</SelectItem>
                                                    <SelectItem value="urgent">Urgent</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium">Issue Description *</Label>
                                            <Textarea
                                                placeholder="What's the issue? Sprinklers not working, pavers shifted, etc."
                                                value={newCallback.reason}
                                                onChange={(e) => setNewCallback({...newCallback, reason: e.target.value})}
                                                rows={2}
                                                className="mt-1 resize-none"
                                            />
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                            <Button variant="outline" size="sm" onClick={() => setShowCallbackForm(false)}>
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleAddCallback}
                                                disabled={addingCallback || !newCallback.reason.trim()}
                                                className="bg-brand-600 hover:bg-brand-700"
                                                size="sm"
                                            >
                                                {addingCallback ? "Saving..." : "Log Callback"}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Callback List - fetched from standalone collection */}
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {projectCallbacks.length > 0 ? (
                                        projectCallbacks.map((cb) => (
                                            <div
                                                key={cb.id || cb._id}
                                                className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow"
                                            >
                                                <div className="flex justify-between items-start gap-2">
                                                    <div className="flex-1 space-y-2">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Badge className={`text-xs ${
                                                                cb.status === 'open' ? 'bg-red-100 text-red-800' :
                                                                cb.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                                                cb.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-green-100 text-green-800'
                                                            }`}>
                                                                {cb.status === 'in_progress' ? 'In Progress' : cb.status?.charAt(0).toUpperCase() + cb.status?.slice(1)}
                                                            </Badge>
                                                            <Badge className={`text-xs ${
                                                                cb.urgency === 'urgent' ? 'bg-red-100 text-red-800' :
                                                                cb.urgency === 'high' ? 'bg-orange-100 text-orange-800' :
                                                                cb.urgency === 'normal' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {cb.urgency === 'urgent' && <AlertTriangle className="h-3 w-3 mr-1" />}
                                                                {cb.urgency}
                                                            </Badge>
                                                            {cb.reported_by && (
                                                                <span className="text-xs text-muted-foreground">by {cb.reported_by}</span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{cb.issue_description}</p>
                                                        {cb.resolution_notes && (
                                                            <div className="bg-green-50 rounded p-2">
                                                                <p className="text-xs font-medium text-green-800">Resolution:</p>
                                                                <p className="text-xs text-green-700">{cb.resolution_notes}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-10 w-10 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                                                        onClick={() => handleDeleteCallback(cb.id || cb._id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                <div className="flex items-center gap-2 mt-3 pt-3 border-t text-xs text-muted-foreground flex-wrap">
                                                    <Clock className="h-3 w-3" />
                                                    <span>
                                                        {cb.created_at ? new Date(cb.created_at).toLocaleString('en-US', {
                                                            month: 'short', day: 'numeric', year: 'numeric',
                                                            hour: 'numeric', minute: '2-digit', hour12: true
                                                        }) : 'Unknown date'}
                                                    </span>
                                                    {cb.scheduled_date && (
                                                        <span className="text-blue-600">| Scheduled: {new Date(cb.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="bg-white border rounded-lg p-8 text-center">
                                            <PhoneIncoming className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-sm text-muted-foreground">No callbacks for this project</p>
                                            <p className="text-xs text-muted-foreground mt-1">Click "Log Callback" when a customer reports an issue</p>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="companycam" className="space-y-6 mt-0">
                                {/* Notes Section */}
                                <div>
                                    <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                                        <StickyNote className="h-4 w-4" />
                                        Project Notes
                                    </h3>

                                    {/* Add Note Input */}
                                    <div className="bg-white border rounded-lg p-4 sm:p-5 mb-4">
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <Textarea
                                                placeholder="Type a note..."
                                                value={newNoteContent}
                                                onChange={(e) => setNewNoteContent(e.target.value)}
                                                rows={2}
                                                className="resize-none flex-1"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.ctrlKey && newNoteContent.trim()) {
                                                        handleAddNote();
                                                    }
                                                }}
                                            />
                                            <Button
                                                onClick={handleAddNote}
                                                disabled={addingNote || !newNoteContent.trim()}
                                                className="bg-brand-600 hover:bg-brand-700 self-end h-10"
                                            >
                                                {addingNote ? (
                                                    "Adding..."
                                                ) : (
                                                    <>
                                                        <Plus className="h-4 w-4 mr-1" />
                                                        Add Note
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-2">Press Ctrl+Enter to add quickly</p>
                                    </div>

                                    {/* Notes List */}
                                    <div className="space-y-3 max-h-64 overflow-y-auto">
                                        {Array.isArray(selectedProject?.notes) && selectedProject.notes.length > 0 ? (
                                            selectedProject.notes.map((note, index) => (
                                                <div
                                                    key={note.id || index}
                                                    className="bg-white border rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow"
                                                >
                                                    <div className="flex justify-between items-start gap-2">
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1">
                                                            {note.content}
                                                        </p>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-10 w-10 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                                                            onClick={() => handleDeleteNote(note.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-3 pt-3 border-t text-xs text-muted-foreground">
                                                        <Clock className="h-3 w-3" />
                                                        <span>
                                                            {note.created_at ? new Date(note.created_at).toLocaleString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            }) : 'Unknown date'}
                                                        </span>
                                                        {note.created_by && (
                                                            <>
                                                                <span>|</span>
                                                                <span>{note.created_by}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="bg-white border rounded-lg p-8 text-center">
                                                <StickyNote className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                                <p className="text-sm text-muted-foreground">No notes yet</p>
                                                <p className="text-xs text-muted-foreground mt-1">Add a note above to get started</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* CompanyCam Pictures Section */}
                                <div>
                                    <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                                        <Camera className="h-4 w-4" />
                                        CompanyCam Pictures
                                    </h3>
                                    <div className="bg-white border rounded-lg p-4 sm:p-5">
                                        <CompanyCamTab
                                            project={selectedProject}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            </div>
                        </Tabs>
                    )
                )}
            </DialogContent>
        </Dialog>

        {/* Create Change Order Dialog */}
        <CreateChangeOrderDialog
            open={isCreateChangeOrderOpen}
            onOpenChange={setIsCreateChangeOrderOpen}
            project={selectedProject}
            customer={selectedProject?.customer}
            onSaveSuccess={() => {
                if (refreshSelectedProject) {
                    refreshSelectedProject();
                }
                fetchChangeOrders();
            }}
        />

        {/* View Change Order Dialog */}
        <ViewChangeOrderDialog
            open={isViewChangeOrderOpen}
            onOpenChange={setIsViewChangeOrderOpen}
            changeOrder={selectedChangeOrder}
            project={selectedProject}
            customer={selectedProject?.customer}
            onEdit={(changeOrder) => handleEditChangeOrderDocument(changeOrder)}
            onDelete={(changeOrder) => handleDeleteChangeOrderDocument(changeOrder.id)}
        />

        {/* Edit Change Order Dialog */}
        <EditChangeOrderDialog
            open={isEditChangeOrderOpen}
            onOpenChange={setIsEditChangeOrderOpen}
            changeOrder={changeOrderToEdit}
            project={selectedProject}
            customer={selectedProject?.customer}
            onSaveSuccess={() => {
                fetchChangeOrders();
                if (refreshSelectedProject) {
                    refreshSelectedProject();
                }
            }}
        />

        {/* Send Payment Receipt Dialog */}
        {selectedPaymentForReceipt && selectedProject?.customer && (
            <SendPaymentReceiptDialog
                open={isSendReceiptDialogOpen}
                onOpenChange={setIsSendReceiptDialogOpen}
                payment={selectedPaymentForReceipt}
                project={selectedProject}
                customer={selectedProject.customer}
                onReceiptSent={() => { fetchEmailHistory(); refreshSelectedProject(); }}
            />
        )}

        {/* Appointment Dialog */}
        <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{editingEventIndex !== null ? 'Edit' : 'Add'} Event/Appointment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div>
                        <Label>Type *</Label>
                        <Select
                            value={newEvent.type}
                            onValueChange={(value) => setNewEvent({ ...newEvent, type: value, scheduled_time: '' })}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="event">📌 Event (date only)</SelectItem>
                                <SelectItem value="appointment">📅 Appointment (date + time)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="title">Title *</Label>
                        <Input
                            id="title"
                            value={newEvent.title}
                            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                            placeholder="e.g., Site Visit, Contract Signed"
                        />
                    </div>
                    <div>
                        <Label htmlFor="description">Description</Label>
                        <Input
                            id="description"
                            value={newEvent.description}
                            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                            placeholder="Optional details"
                        />
                    </div>
                    <div className={newEvent.type === 'appointment' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''}>
                        <div>
                            <Label htmlFor="scheduled_date">Date *</Label>
                            <Input
                                id="scheduled_date"
                                type="date"
                                value={newEvent.scheduled_date}
                                onChange={(e) => setNewEvent({ ...newEvent, scheduled_date: e.target.value })}
                            />
                        </div>
                        {newEvent.type === 'appointment' && (
                            <div>
                                <Label htmlFor="scheduled_time">Time *</Label>
                                <TimePicker
                                    id="scheduled_time"
                                    value={newEvent.scheduled_time}
                                    onChange={(value) => setNewEvent({ ...newEvent, scheduled_time: value })}
                                    placeholder="Select time"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setIsEventDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveEvent}>
                        Add to Timeline
                    </Button>
                </div>
            </DialogContent>
        </Dialog>

        {/* Delete Change Order Confirmation Dialog */}
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete Change Order</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this change order? This action cannot be undone and will recalculate the project total.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-3 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            setIsDeleteConfirmOpen(false);
                            setChangeOrderToDelete(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={confirmDeleteChangeOrder}
                    >
                        Delete
                    </Button>
                </div>
            </DialogContent>
        </Dialog>

        {/* Delete Event Confirmation Dialog */}
        <Dialog open={isDeleteEventDialogOpen} onOpenChange={setIsDeleteEventDialogOpen}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete Event</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this event from the timeline? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-3 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            setIsDeleteEventDialogOpen(false);
                            setEditingEventIndex(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={confirmDeleteEvent}
                    >
                        Delete
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default ProjectDetailDialog;
